/**
 * PhoneInputSplit — iter334
 *
 * Split phone-number input: country-code pill + local-number field.
 * Country code auto-defaults from the ad's selected `country` ISO-2
 * and is editable via the premium `CurrencyBottomSheet` (dial_code
 * mode). Local number accepts subscriber digits only; a leading zero
 * is tolerated and stripped at normalization time.
 *
 * Props
 *  - countryIso2: string (ISO-2 of the listing — e.g. "SD" "EG")
 *  - countriesMeta: object — `{SD:{dial_code,...}, EG:{...}, ...}`
 *                   typically the `countries` field from
 *                   GET /api/meta/country-currencies.
 *  - value: { country_code, local }   (current state)
 *  - onChange: ({ country_code, local }) => void
 *  - placeholder: string (already translated)
 *  - error: boolean
 *  - errorMessage: string (already translated)
 *  - isRTL: boolean
 *  - testId: string (root data-testid)
 *  - autoFocus: boolean
 */
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { Phone, ChevronDown } from 'lucide-react';
import CurrencyBottomSheet from './CurrencyBottomSheet';
import './PhoneInputSplit.css';

export default function PhoneInputSplit({
  countryIso2,
  countriesMeta,
  value,
  onChange,
  placeholder,
  error,
  errorMessage,
  isRTL = false,
  testId = 'phone-input-split',
  autoFocus = false,
}) {
  const [sheetOpen, setSheetOpen] = useState(false);

  // Auto-default country code from the listing's selected country.
  // The seller can override later by tapping the pill.
  useEffect(() => {
    if (!countriesMeta || !countryIso2) return;
    const meta = countriesMeta[(countryIso2 || '').toUpperCase()];
    if (!meta) return;
    const expected = meta.dial_code;
    if (!value?.country_code || value.country_code !== expected) {
      // Only auto-fill if the seller hasn't manually overridden.
      // Heuristic: if the current code matches a different known
      // dial code AND `local` already has digits, leave it alone.
      const hasManualOverride =
        value?.country_code &&
        value.country_code !== expected &&
        (value.local || '').replace(/\D/g, '').length >= 3;
      if (!hasManualOverride) {
        onChange({ country_code: expected, local: value?.local || '' });
      }
    }
  }, [countryIso2, countriesMeta]); // value intentionally excluded — we only react to country changes.

  // Build the dial-code list for the picker, sorted with the listing's
  // country pinned to the top.
  const dialCodeItems = useMemo(() => {
    if (!countriesMeta) return [];
    const entries = Object.entries(countriesMeta).map(([iso2, meta]) => ({
      iso2,
      dial_code: meta.dial_code,
      country_name_en: meta.country_name_en,
      country_name_ar: meta.country_name_ar,
      flag: meta.flag,
      code: meta.dial_code, // for compatibility with sheet keying
      recommended: iso2 === (countryIso2 || '').toUpperCase(),
    }));
    entries.sort((a, b) => {
      if (a.recommended && !b.recommended) return -1;
      if (!a.recommended && b.recommended) return 1;
      return a.country_name_en.localeCompare(b.country_name_en);
    });
    return entries;
  }, [countriesMeta, countryIso2]);

  const handleLocalChange = useCallback(
    (e) => {
      // Strip everything except digits + whitespace. Leading zero
      // is preserved here; backend normalizer drops it.
      const cleaned = e.target.value.replace(/[^\d\s]/g, '');
      onChange({ country_code: value?.country_code, local: cleaned });
    },
    [onChange, value]
  );

  const flag =
    (countriesMeta &&
      Object.values(countriesMeta).find(
        (m) => m.dial_code === value?.country_code
      )?.flag) ||
    '🏳️';

  return (
    <div
      className={`iter334-phone ${isRTL ? 'is-rtl' : ''} ${error ? 'has-error' : ''}`}
      data-testid={testId}
    >
      <div className="iter334-phone-row">
        <button
          type="button"
          className="iter334-phone-codepill"
          onClick={() => setSheetOpen(true)}
          aria-label={isRTL ? 'تغيير رمز الدولة' : 'Change country code'}
          data-testid={`${testId}-code-button`}
        >
          <span className="iter334-phone-codepill-flag" aria-hidden="true">
            {flag}
          </span>
          <span className="iter334-phone-codepill-code" data-testid={`${testId}-code-value`}>
            {value?.country_code || '+249'}
          </span>
          <ChevronDown size={14} className="iter334-phone-codepill-chev" />
        </button>
        <div className="iter334-phone-input-wrap">
          <Phone size={16} className="iter334-phone-input-icon" aria-hidden="true" />
          <input
            type="tel"
            inputMode="numeric"
            autoComplete="tel-national"
            className="iter334-phone-input"
            value={value?.local || ''}
            onChange={handleLocalChange}
            placeholder={placeholder}
            autoFocus={autoFocus}
            data-testid={`${testId}-local-input`}
            dir="ltr"
          />
        </div>
      </div>
      {error && errorMessage && (
        <div className="iter334-phone-error" data-testid={`${testId}-error`}>
          {errorMessage}
        </div>
      )}

      <CurrencyBottomSheet
        open={sheetOpen}
        onClose={() => setSheetOpen(false)}
        onSelect={(item) =>
          onChange({ country_code: item.dial_code, local: value?.local || '' })
        }
        items={dialCodeItems}
        title={isRTL ? 'اختر رمز الدولة' : 'Select country code'}
        searchPlaceholder={isRTL ? 'بحث (دولة أو رمز)' : 'Search country or code'}
        selectedCode={value?.country_code}
        mode="dial_code"
        isRTL={isRTL}
      />
    </div>
  );
}
